import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { Select, Typography, Collapse, InputNumber, Switch, Checkbox } from "antd";
import { usePermissions } from "./PermissionsProvider";
import { ConfigService } from "./services/ConfigService";
import { ModelConfig } from "./model_gen/config";
import { Agent } from "./model_gen/agent_base";

const { Panel } = Collapse;

type Category = "agents" | "finetunes" | "models";

interface AgentSettings {
  max_iterations?: number | null;
  execution_mode?: string | null;
  high_performance?: boolean | null;
  disable_chaining?: boolean | null;
  model?: string | null;
}

interface ModelInputProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  style?: React.CSSProperties;
  companyId?: number;
  showEmptyOption?: boolean;
  intentGroupId?: number;
  modelType?: "all" | "server" | "client";
  agentSettings?: AgentSettings | null;
  onAgentParamsChange?: (params: {
    agent_id: number | null;
    settings: AgentSettings | null;
  }) => void;
  onCategoryChange?: (category: Category) => void;
}

const ModelInput: React.FC<ModelInputProps> = ({
  value,
  onChange,
  placeholder = "Select a model",
  label = "Model",
  disabled = false,
  style = {},
  companyId = -1,
  showEmptyOption = true,
  intentGroupId,
  modelType = "all",
  agentSettings: externalAgentSettings,
  onAgentParamsChange,
  onCategoryChange,
}) => {
  const { apiKey } = usePermissions();
  const [models, setModels] = useState<string[]>([]);
  const [modelFinetuned, setModelFinetuned] = useState<Record<string, boolean>>({});
  const [agents, setAgents] = useState<Agent[]>([]);
  const [agentReleases, setAgentReleases] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [showSubagents, setShowSubagents] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<Category>("agents");
  const categoryInitialized = useRef(false);

  const agentSettings = useMemo(() => {
    return externalAgentSettings || {};
  }, [externalAgentSettings]);

  // Auto-detect category from value once data is loaded
  useEffect(() => {
    if (categoryInitialized.current) return;
    if (!value) return;
    if (value.startsWith("agent:")) {
      setSelectedCategory("agents");
      onCategoryChange?.("agents");
      categoryInitialized.current = true;
    } else if (Object.keys(modelFinetuned).length > 0) {
      const cat = modelFinetuned[value] ? "finetunes" : "models";
      setSelectedCategory(cat);
      onCategoryChange?.(cat);
      categoryInitialized.current = true;
    }
  }, [value, modelFinetuned]);

  // Fetch models
  useEffect(() => {
    setIsLoading(true);
    const sub = ConfigService.getModels(
      intentGroupId ?? companyId,
      null,
      apiKey
    ).subscribe(
      (modelData: ModelConfig) => {
        let modelsToDisplay: string[] = [];
        if (modelType === "server" && modelData.server_models) {
          modelsToDisplay = modelData.server_models;
        } else if (modelType === "client" && modelData.client_models) {
          modelsToDisplay = modelData.client_models;
        } else {
          modelsToDisplay = modelData.all_models || [];
        }
        setModels(modelsToDisplay);
        setModelFinetuned(modelData.model_finetuned || {});
        setIsLoading(false);
      },
      (error: any) => {
        console.error("Error fetching models:", error);
        setIsLoading(false);
      }
    );
    return () => sub.unsubscribe();
  }, [apiKey, companyId, intentGroupId, modelType]);

  // Fetch agents and releases
  const releasesSubscriptionRef = useRef<any>(null);
  useEffect(() => {
    if (!apiKey) return;

    const agentsSub = ConfigService.getAgents(null, apiKey, showSubagents).subscribe(
      (agentsResponse: any) => {
        const fetchedAgents = Array.isArray(agentsResponse)
          ? agentsResponse
          : agentsResponse.agents || [];
        setAgents(fetchedAgents);

        releasesSubscriptionRef.current = ConfigService.getAgentsWithReleases(
          null, apiKey, showSubagents
        ).subscribe(
          (releasesResponse: any) => {
            const fetchedReleases = Array.isArray(releasesResponse)
              ? releasesResponse
              : releasesResponse.agent_releases || [];

            const agentIdsWithReleases = new Set(
              fetchedReleases.map((r: any) => r.agent_id)
            );
            const draftEntries = fetchedAgents
              .filter((a: Agent) => !agentIdsWithReleases.has(a.id))
              .map((agent: Agent) => ({
                agent_id: agent.id,
                agent_name: agent.agent_name,
                description: agent.description,
                application_id: agent.application_id,
                application_name: agent.application_name,
                status: agent.status,
                date_created: agent.date_created,
                conversation_count: 0,
                is_subagent: false,
                release_name: "draft",
                release_version: null,
                release_is_default: true,
              }));

            setAgentReleases([...fetchedReleases, ...draftEntries]);
          },
          (error: any) => {
            console.error("Error fetching agent releases:", error);
            const draftEntries = fetchedAgents.map((agent: Agent) => ({
              agent_id: agent.id,
              agent_name: agent.agent_name,
              description: agent.description,
              application_id: agent.application_id,
              application_name: agent.application_name,
              status: agent.status,
              date_created: agent.date_created,
              conversation_count: 0,
              is_subagent: false,
              release_name: "draft",
              release_version: null,
              release_is_default: true,
            }));
            setAgentReleases(draftEntries);
          }
        );
      },
      (error: any) => {
        console.error("Error fetching agents:", error);
      }
    );

    return () => {
      agentsSub.unsubscribe();
      if (releasesSubscriptionRef.current) {
        releasesSubscriptionRef.current.unsubscribe();
        releasesSubscriptionRef.current = null;
      }
    };
  }, [apiKey, showSubagents]);

  // Helpers
  const capitalizeFirstLetter = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

  const isAgentValue = (v?: string) => v?.startsWith("agent:");

  const getSelectedAgentFromValue = (v?: string) => {
    if (!isAgentValue(v)) return null;
    if (v?.includes(":")) {
      const parts = v.split(":");
      if (parts.length >= 3) {
        const agentId = parts[1];
        const releaseName = parts.slice(2).join(":");
        const release = agentReleases.find(
          (r) => r.agent_id?.toString() === agentId && r.release_name === releaseName
        );
        if (release) {
          return {
            id: release.agent_id,
            agent_name: release.agent_name,
            description: release.description,
            application_id: release.application_id,
            application_name: release.application_name,
            status: release.status,
            date_created: release.date_created,
            conversation_count: release.conversation_count,
            meta: {},
          } as Agent;
        }
      }
    }
    const agentId = v?.replace("agent:", "");
    return agents.find((a) => a.id?.toString() === agentId) || null;
  };

  // Update selected agent when value changes
  useEffect(() => {
    setSelectedAgent(getSelectedAgentFromValue(value));
  }, [value, agents, agentReleases]);

  const groupModelsByProvider = (list: string[]) => {
    const grouped: Record<string, string[]> = {};
    list.forEach((m) => {
      const [provider] = m.split(":");
      if (!grouped[provider]) grouped[provider] = [];
      if (!grouped[provider].includes(m)) grouped[provider].push(m);
    });
    return grouped;
  };

  // Filtered model lists
  const finetunedModels = useMemo(
    () => models.filter((m) => modelFinetuned[m]),
    [models, modelFinetuned]
  );
  const standardModels = useMemo(
    () => models.filter((m) => !modelFinetuned[m]),
    [models, modelFinetuned]
  );

  const { groupedStandard, sortedStandardProviders } = useMemo(() => {
    const grouped = groupModelsByProvider(standardModels);
    const sorted = Object.keys(grouped).sort((a, b) => {
      if (a.toLowerCase() === "maitai") return -1;
      if (b.toLowerCase() === "maitai") return 1;
      return a.localeCompare(b);
    });
    return { groupedStandard: grouped, sortedStandardProviders: sorted };
  }, [standardModels]);

  const { groupedFinetunes, sortedFinetuneProviders } = useMemo(() => {
    const grouped = groupModelsByProvider(finetunedModels);
    const sorted = Object.keys(grouped).sort((a, b) => a.localeCompare(b));
    return { groupedFinetunes: grouped, sortedFinetuneProviders: sorted };
  }, [finetunedModels]);

  // Category change
  const handleCategoryChange = (cat: Category) => {
    setSelectedCategory(cat);
    onCategoryChange?.(cat);
    if (onChange) onChange("");
    if (onAgentParamsChange) onAgentParamsChange({ agent_id: null, settings: null });
  };

  // Item selection
  const handleValueChange = (newValue: string) => {
    if (onChange) onChange(newValue);
    if (onAgentParamsChange) {
      if (isAgentValue(newValue)) {
        const agent = getSelectedAgentFromValue(newValue);
        onAgentParamsChange({
          agent_id: agent?.id ? Number(agent.id) : null,
          settings: {
            max_iterations: agent?.max_iterations || null,
            execution_mode: "route",
            high_performance: true,
            disable_chaining: true,
            model: "openai:gpt-4o-mini",
          },
        });
      } else {
        onAgentParamsChange({ agent_id: null, settings: null });
      }
    }
  };

  const handleAgentSettingChange = useCallback(
    (key: keyof AgentSettings, newValue: any) => {
      if (onAgentParamsChange) {
        onAgentParamsChange({
          agent_id: selectedAgent?.id ? Number(selectedAgent.id) : null,
          settings: { ...agentSettings, [key]: newValue },
        });
      }
    },
    [agentSettings, selectedAgent?.id, onAgentParamsChange]
  );

  // Build item options based on category
  const renderItemOptions = () => {
    if (selectedCategory === "agents") {
      if (agentReleases.length === 0) return null;
      return (
        <Select.OptGroup
          key="agents"
          label={
            <Typography.Text strong style={{ color: "#1890ff" }}>
              Agent Releases
            </Typography.Text>
          }>
          {agentReleases.map((release) => (
            <Select.Option
              key={`agent:${release.agent_id}:${release.release_name}`}
              value={`agent:${release.agent_id}:${release.release_name}`}
              label={`${release.agent_name} (${release.release_name})`}>
              {release.agent_name} ({release.release_name})
              {release.release_is_default && " (default)"}
            </Select.Option>
          ))}
        </Select.OptGroup>
      );
    }

    if (selectedCategory === "finetunes") {
      const seen = new Set<string>();
      const deduped: Array<{ value: string; label: string }> = [];
      finetunedModels.forEach((model) => {
        const [, ...parts] = model.split(":");
        const name = parts.join(":");
        if (!seen.has(name)) {
          seen.add(name);
          deduped.push({ value: model, label: name });
        }
      });
      return deduped.map((item) => (
        <Select.Option key={item.value} value={item.value} label={item.label}>
          {item.label}
        </Select.Option>
      ));
    }

    return sortedStandardProviders.map((provider) => (
      <Select.OptGroup
        key={provider}
        label={capitalizeFirstLetter(provider)}>
        {groupedStandard[provider].map((model) => {
          const [, ...parts] = model.split(":");
          return (
            <Select.Option key={model} value={model} label={model}>
              {parts.join(":")}
            </Select.Option>
          );
        })}
      </Select.OptGroup>
    ));
  };

  const categoryOptions = [
    { value: "agents", label: "Agents" },
    { value: "finetunes", label: "Finetunes" },
    { value: "models", label: "Models" },
  ];

  const labelStyle = {
    fontSize: "14px",
    fontWeight: 600 as const,
    color: "rgba(0,0,0,0.8)",
    marginBottom: "4px",
    display: "block",
  };

  return (
    <div style={style}>
      {label && <Typography.Text strong>{label}</Typography.Text>}
      <div
        style={{
          display: "flex",
          flexDirection: "column" as const,
          gap: "12px",
          marginTop: label ? "8px" : 0,
        }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "4px" }}>
            <span style={labelStyle}>Type</span>
            {selectedCategory === "agents" && (
              <Checkbox
                checked={showSubagents}
                onChange={(e) => setShowSubagents(e.target.checked)}
                style={{ whiteSpace: "nowrap" }}>
                <span style={{ fontSize: "11px", color: "rgba(0,0,0,0.4)" }}>
                  Include subagents
                </span>
              </Checkbox>
            )}
          </div>
          <Select
            value={selectedCategory}
            onChange={(v) => handleCategoryChange(v as Category)}
            disabled={disabled}
            style={{ width: "100%" }}
            options={categoryOptions}
          />
        </div>

        <div>
          <span style={labelStyle}>Model</span>
          <Select
            value={value || undefined}
            style={{ width: "100%" }}
            onChange={handleValueChange}
            disabled={disabled}
            loading={isLoading}
            placeholder={placeholder}
            optionLabelProp="label"
            optionFilterProp="children"
            dropdownStyle={{ width: "400px", maxWidth: "80vw" }}
            showSearch
            allowClear>
            {showEmptyOption && (
              <Select.Option key="" value="" label="None">
                None
              </Select.Option>
            )}
            {renderItemOptions()}
          </Select>
        </div>
      </div>

      {isAgentValue(value) && selectedAgent && (
        <div style={{ marginTop: "16px" }}>
          <Collapse ghost>
            <Panel
              header={
                <Typography.Text strong style={{ color: "#1890ff" }}>
                  Agent Settings
                </Typography.Text>
              }
              key="agent-settings">
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                }}>
                <div>
                  <Typography.Text
                    strong
                    style={{ display: "block", marginBottom: "8px" }}>
                    Max Iterations
                  </Typography.Text>
                  <InputNumber
                    value={agentSettings.max_iterations}
                    onChange={(v) => handleAgentSettingChange("max_iterations", v)}
                    min={1}
                    max={100}
                    style={{ width: "100%" }}
                    placeholder="Maximum number of iterations"
                  />
                </div>

                <div>
                  <Typography.Text
                    strong
                    style={{ display: "block", marginBottom: "8px" }}>
                    Execution Mode
                  </Typography.Text>
                  <Select
                    value={agentSettings.execution_mode}
                    onChange={(v) => handleAgentSettingChange("execution_mode", v)}
                    style={{ width: "100%" }}
                    placeholder="Select execution mode"
                    allowClear>
                    <Select.Option value="auto">Auto</Select.Option>
                    <Select.Option value="route">Route</Select.Option>
                    <Select.Option value="reasoning">Reasoning</Select.Option>
                  </Select>
                </div>

                <div>
                  <Typography.Text
                    strong
                    style={{ display: "block", marginBottom: "8px" }}>
                    High Performance
                  </Typography.Text>
                  <Switch
                    checked={agentSettings.high_performance || false}
                    onChange={(checked) =>
                      handleAgentSettingChange("high_performance", checked)
                    }
                  />
                </div>

                <div>
                  <Typography.Text
                    strong
                    style={{ display: "block", marginBottom: "8px" }}>
                    Disable Chaining
                  </Typography.Text>
                  <Switch
                    checked={agentSettings.disable_chaining || false}
                    onChange={(checked) =>
                      handleAgentSettingChange("disable_chaining", checked)
                    }
                  />
                </div>
              </div>
            </Panel>
          </Collapse>
        </div>
      )}
    </div>
  );
};

export default ModelInput;
